import React from 'react'
import { motion } from 'framer-motion'
import { ArrowRight, Zap, Moon, Sun } from 'lucide-react'

const Hero = () => {
  const scrollToSection = (href) => {
    const element = document.querySelector(href)
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }

  return (
    <section className="min-h-screen bg-hero-gradient relative overflow-hidden flex items-center">
      {/* Animated Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-20 left-20 w-32 h-32 bg-energy-yellow/20 rounded-full animate-float"></div>
        <div className="absolute top-40 right-32 w-24 h-24 bg-energy-orange/30 rounded-full animate-float" style={{ animationDelay: '2s' }}></div>
        <div className="absolute bottom-32 left-32 w-40 h-40 bg-glow-green/10 rounded-full animate-float" style={{ animationDelay: '4s' }}></div>
        
        {/* Floating Icons */}
        <motion.div 
          className="absolute top-32 right-20"
          animate={{ y: [-20, 20, -20] }}
          transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
        >
          <Sun className="w-12 h-12 text-energy-yellow/40" />
        </motion.div>
        
        <motion.div 
          className="absolute bottom-40 right-40"
          animate={{ y: [20, -20, 20] }}
          transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
        >
          <Moon className="w-10 h-10 text-soft-blue/60" />
        </motion.div>
        
        <motion.div 
          className="absolute top-1/2 left-20"
          animate={{ rotate: [0, 360] }}
          transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
        >
          <Zap className="w-8 h-8 text-energy-orange/50" />
        </motion.div>
      </div>

      <div className="container-max relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <motion.div 
            className="text-center lg:text-left"
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
          >
            {/* Company Name */}
            <motion.div 
              className="mb-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <h3 className="text-lg lg:text-xl text-white/80 font-medium mb-2">
                G-GES Sürdürülebilir Enerji Çözümleri A.Ş.
              </h3>
            </motion.div>

            {/* Main Headline */}
            <motion.h1 
              className="text-4xl lg:text-6xl xl:text-7xl font-bold text-white mb-6 leading-tight"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
            >
              Extend the{' '}
              <span className="text-gradient bg-gradient-to-r from-energy-yellow to-energy-orange bg-clip-text text-transparent">
                Daylight
              </span>{' '}
              with{' '}
              <span className="relative">
                Pigmentium™
                <motion.div 
                  className="absolute -bottom-2 left-0 right-0 h-1 bg-energy-yellow rounded-full"
                  initial={{ scaleX: 0 }}
                  animate={{ scaleX: 1 }}
                  transition={{ duration: 0.8, delay: 1 }}
                />
              </span>
            </motion.h1>

            {/* Subheadline */}
            <motion.p 
              className="text-lg lg:text-xl text-white/90 mb-8 max-w-xl mx-auto lg:mx-0"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.5 }}
            >
              Night-time Energy Generation with Solar Power Absorbed in Just{' '}
              <span className="font-bold text-energy-yellow">5 Seconds</span>
            </motion.p>

            {/* Key Benefits */}
            <motion.div 
              className="flex flex-wrap justify-center lg:justify-start gap-4 mb-8"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.6 }}
            >
              <div className="bg-white/10 backdrop-blur-sm rounded-full px-4 py-2 text-white text-sm">
                500 Lux for 12 Hours
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-full px-4 py-2 text-white text-sm">
                Eco-Friendly
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-full px-4 py-2 text-white text-sm">
                Non-Toxic
              </div>
            </motion.div>

            {/* CTA Buttons */}
            <motion.div 
              className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.7 }}
            >
              <button 
                onClick={() => scrollToSection('#solution')}
                className="btn-primary group"
              >
                Discover the Technology
                <ArrowRight className="ml-2 w-5 h-5 transition-transform group-hover:translate-x-1" />
              </button>
              <button 
                onClick={() => scrollToSection('#contact')}
                className="btn-secondary"
              >
                Get in Touch
              </button>
            </motion.div>
          </motion.div>

          {/* Right Content - Visual */}
          <motion.div 
            className="relative"
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            <div className="relative mx-auto max-w-md lg:max-w-lg">
              {/* Solar Panel Representation */}
              <div className="relative bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl p-8 shadow-2xl">
                <div className="grid grid-cols-3 gap-2 mb-6">
                  {[...Array(9)].map((_, i) => (
                    <motion.div
                      key={i}
                      className="aspect-square bg-gradient-to-br from-blue-900 to-blue-800 rounded-lg"
                      animate={{ opacity: [0.5, 1, 0.5] }}
                      transition={{ 
                        duration: 2, 
                        repeat: Infinity, 
                        delay: i * 0.2,
                        ease: "easeInOut" 
                      }}
                    />
                  ))}
                </div>
                
                {/* Glowing Effect */}
                <motion.div 
                  className="absolute inset-0 bg-energy-yellow/20 rounded-2xl blur-xl"
                  animate={{ opacity: [0.3, 0.7, 0.3] }}
                  transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                />
                
                {/* Energy Rays */}
                <div className="absolute -top-4 -right-4">
                  <motion.div 
                    className="w-24 h-24 border-4 border-energy-yellow/50 rounded-full"
                    animate={{ rotate: 360 }}
                    transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
                  />
                  <motion.div 
                    className="absolute inset-2 w-16 h-16 border-2 border-energy-orange/30 rounded-full"
                    animate={{ rotate: -360 }}
                    transition={{ duration: 6, repeat: Infinity, ease: "linear" }}
                  />
                </div>
              </div>

              {/* Night Mode Indicator */}
              <motion.div 
                className="absolute -bottom-6 left-1/2 transform -translate-x-1/2 bg-night-blue rounded-full px-4 py-2 text-white text-sm font-medium shadow-lg"
                animate={{ y: [-5, 5, -5] }}
                transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
              >
                🌙 Night Energy Active
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <motion.div 
        className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
        animate={{ y: [0, 10, 0] }}
        transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
      >
        <div className="w-6 h-10 border-2 border-white/50 rounded-full flex justify-center">
          <div className="w-1 h-3 bg-white/50 rounded-full mt-2"></div>
        </div>
      </motion.div>
    </section>
  )
}

export default Hero 